import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDarkMode } from './DarkModeContext';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';
// DECISIONHIST: the same green/grey pair LeaveCancellationHistorySection already uses for a
// settled outcome, so a decision reads the same way in both history sections.
const OK_BG = '#d1fae5';
const OK_FG = '#065f46';
const NO_BG = '#e5e7eb';
const NO_FG = '#4b5563';

function fmt(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

function fmtDateTime(d) {
  if (!d) return '-';
  const dt = toDisplayDate(d);
  return `${dt.toLocaleDateString('en-GB')} ${dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

// DECISIONHIST: a fourth, separate section on the Validation page, a READ ONLY record of the
// holiday LEAVE REQUESTS (not cancellations) this manager has actually decided.
//
// Deliberately a SEPARATE section from the cancellation history above it rather than merged into
// one list: approving whether someone may take leave and approving whether an already-granted
// leave may be undone are different questions, backed by different audit action names and
// different declaration fields, and merging them into one query/list would blur exactly the
// "which action type is this" distinction the two backend endpoints each rely on.
export default function LeaveDecisionHistorySection() {
  const { isDarkMode } = useDarkMode();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Same split every other section on this page uses: a failed LOAD must suppress the empty
  // state, so a fetch failure is never mistaken for a genuinely empty history.
  const [loadFailed, setLoadFailed] = useState(false);

  const cardBg = isDarkMode ? '#1a2744' : '#ffffff';
  const textCol = isDarkMode ? '#e8e4d8' : '#1a1a2e';
  const mutedCol = isDarkMode ? '#9ba8c0' : '#666';
  const border = isDarkMode ? '1px solid rgba(201,168,76,0.25)' : '1px solid #e2e6ec';
  const rowBorder = isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f0f0f0';

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/leave/decision-history`)
      .then(res => { setRows(res.data || []); setError(''); setLoadFailed(false); })
      .catch(() => {
        setRows([]);
        setError('Could not load the leave decision history - please refresh.');
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ background: cardBg, border, borderRadius: '10px', padding: '28px', marginBottom: '20px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: 700, color: textCol, margin: '0 0 6px' }}>Leave requests you have decided</h3>
      <p style={{ fontSize: '12px', color: mutedCol, margin: '0 0 16px', lineHeight: 1.5 }}>
        A record of the holiday requests you have already approved or rejected. This does not
        include cancellation requests - those are listed separately below.
      </p>

      {error && <div style={{ fontSize: '13px', color: '#A63A3A', marginBottom: '14px' }} role="alert" aria-live="polite">{error}</div>}

      {loading ? (
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>Loading…</p>
      ) : loadFailed ? null : rows.length === 0 ? (
        // DECISIONHIST: the empty state must read as a normal, expected state, not a failure - it
        // is the normal state for any manager who has not yet decided a holiday request. It says
        // plainly that nothing has been decided YET, so it cannot be mistaken for the separate
        // load-failure state above.
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>
          You have not decided any leave requests yet. Once you approve or reject one, it will
          appear here.
        </p>
      ) : (
        rows.map(r => {
          const approved = r.decision === 'approved';
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: rowBorder, gap: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: textCol }}>
                  {r.employee_name}
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: mutedCol }}>
                  {r.leave_label} · {fmt(r.start_date)} → {fmt(r.end_date)}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: mutedCol }}>
                  Decided {fmtDateTime(r.decided_at)}
                </p>
                {!approved && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: mutedCol, fontStyle: 'italic' }}>
                    {r.reason ? r.reason : 'No reason recorded.'}
                  </p>
                )}
              </div>
              <div style={{ flexShrink: 0 }}>
                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: approved ? OK_BG : NO_BG, color: approved ? OK_FG : NO_FG, whiteSpace: 'nowrap' }}>
                  {approved ? 'Leave approved' : 'Leave rejected'}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
